import { ArrowDown, ArrowUp, Loader2, Plus, Trash } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button, cn } from '@repo/ui'
import type { ProposalSection } from '@/api/proposals/proposals'
import { failureLine } from '@/common/errorMessages'
import { InlineEditableText } from '@/components/proposal/InlineEditableText'
import { CanvasConfirmDialog } from './CanvasConfirmDialog'
import type { ReactNode } from 'react'

// A new section is created with this heading; the caller renames it inline right after.
const newSectionHeading = 'New section'

export function ProposalSectionActions({
  section,
  isFirst,
  isLast,
  disabled = false,
  onRename,
  onDelete,
  onMove,
  heading
}: {
  section: ProposalSection
  isFirst: boolean
  isLast: boolean
  disabled?: boolean
  onRename: (heading: string) => Promise<void>
  onDelete: () => Promise<void>
  onMove: (direction: 'up' | 'down') => Promise<void>
  // Optional wrapper for the row's rename control, e.g. an <h4> the caller renders
  // around it. Wrapping keeps the section's outline semantics without also printing
  // a static copy of the title above the row, and the control inherits the heading's
  // scale rather than the bare row's smaller text.
  heading?: (renameControl: ReactNode) => ReactNode
}): ReactNode {
  const [editing, setEditing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [pending, setPending] = useState<'up' | 'down' | 'delete' | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const blocked = disabled || pending !== null
  // Slotted, the control reads at the caller heading's scale; unslotted, it keeps the
  // bare row's original pair (medium-weight input, plain text-sm display button).
  const editingScale = heading !== undefined ? 'text-base leading-6 font-semibold' : 'text-sm font-medium'
  const displayScale = heading !== undefined ? 'text-base leading-6 font-semibold' : 'text-sm'

  // A disable while the delete dialog is open force-closes it; clear the flag so it does not
  // pop back open once the disable lifts.
  useEffect(() => {
    if (!disabled) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConfirmingDelete(false)
  }, [disabled])

  async function runMove(direction: 'up' | 'down') {
    setPending(direction)
    setFailure(null)
    try {
      await onMove(direction)
    } catch (error) {
      setFailure(failureLine(error, 'The section could not be moved.'))
    } finally {
      setPending(null)
    }
  }

  const renameControl = (
    <InlineEditableText
      value={section.heading}
      editing={editing && !disabled}
      onEditingChange={(next) => {
        if (!disabled) setEditing(next)
      }}
      textClassName={editingScale}
      ariaLabel="Rename section heading"
      onSave={(next) => onRename(next)}
      renderDisplay={(start) => (
        <button
          type="button"
          onClick={() => {
            if (!blocked) start()
          }}
          className={cn(
            'block w-full truncate rounded-xs text-left hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            displayScale
          )}
        >
          {section.heading}
        </button>
      )}
    />
  )

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">{heading !== undefined ? heading(renameControl) : renameControl}</div>
        {!editing && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Move section up"
              disabled={blocked || isFirst}
              onClick={() => void runMove('up')}
            >
              <ArrowUp />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Move section down"
              disabled={blocked || isLast}
              onClick={() => void runMove('down')}
            >
              <ArrowDown />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Delete section ${section.heading}`}
              disabled={blocked}
              onClick={() => {
                setFailure(null)
                setConfirmingDelete(true)
              }}
            >
              <Trash />
            </Button>
          </>
        )}
      </div>
      {/* Move failures surface here; delete failures surface inside the still-open confirm dialog
          and rename failures inside InlineEditableText. */}
      {failure !== null && (
        <p role="alert" className="text-xs text-destructive">
          {failure}
        </p>
      )}
      <CanvasConfirmDialog
        open={confirmingDelete && !disabled}
        title={`Delete “${section.heading}”?`}
        body={`This permanently removes “${section.heading}” and its chat history.`}
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          setPending('delete')
          try {
            await onDelete()
          } finally {
            setPending(null)
          }
        }}
        onOpenChange={(next) => {
          if (!next) setConfirmingDelete(false)
        }}
      />
    </div>
  )
}

export function AddSectionRow({
  disabled = false,
  onAdd
}: {
  disabled?: boolean
  onAdd: (heading: string) => Promise<void>
}): ReactNode {
  const [adding, setAdding] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  async function add() {
    if (adding) return
    setAdding(true)
    setFailure(null)
    try {
      await onAdd(newSectionHeading)
    } catch (error) {
      setFailure(failureLine(error, 'The section could not be added.'))
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <Button type="button" variant="outline" size="sm" disabled={disabled || adding} onClick={() => void add()}>
        {adding ? (
          <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        ) : (
          <Plus aria-hidden="true" />
        )}
        Add section
      </Button>
      {failure !== null && (
        <p role="alert" className="text-xs text-destructive">
          {failure}
        </p>
      )}
    </div>
  )
}
